<template>
  <div
    v-loading="!isLoaded"
    class="container-sources"
  >
    <div v-if="isLoaded">
      <tf-table
        v-if="!hasNonCensusSources"
        v-bind="{
          tableData: [],
          tableMeta: { columns: [] },
        }"
      >
        <template #title>
          {{ categoryType | capitalize }} files
        </template>
        <template
          v-if="error"
          #error
        >
          {{ error }}
        </template>
        <empty-table-cell slot="empty">
          There doesn't appear to be any {{ categoryType }} files.
          <router-link
            slot="cta"
            :to="{
              name: 'AddCoverageSources',
              params: {
                ...$route.params, // productTypeId is optional so gets 'removed' on route change.
                categoryType,
                documentId,
              },
            }"
          >
            <tf-button
              type="secondary"
              icon="plus"
              size="medium"
            >
              Add files
            </tf-button>
          </router-link>
        </empty-table-cell>
        <template slot="title">
          {{ categoryType | capitalize }} files
        </template>
      </tf-table>
      <template v-else>
        <div
          v-for="({ sources, type }, idx) in sourceTypes.filter(({ sources }) => sources.length)"
          :key="type"
          class="container-source-type"
        >
          <tf-table
            v-if="sources.length"
            v-bind="{
              tableData: sources,
              tableMeta: {
                columns: [
                  {
                    label: $options.filters.sourceType(type),
                    prop: 'name',
                  },
                  {
                    prop: null,
                    width: '30',
                  },
                ],
              },
            }"
          >
            <template
              v-if="error"
              #error
            >
              {{ error }}
            </template>
            <template
              v-if="!idx"
              #title
            >
              {{ categoryType | capitalize }} files
            </template>
            <router-link
              v-if="hasNonCensusSources && !idx"
              slot="ctaHeader"
              class="router-link-add-files"
              :to="{
                name: 'AddCoverageSources',
                params: {
                  ...$route.params, // productTypeId is optional so gets 'removed' on route change.
                  categoryType,
                  documentId,
                },
              }"
            >
              <tf-button
                type="primary"
                icon="plus"
                size="text-icon"
              >
                Add files
              </tf-button>
            </router-link>
            <source-table-row
              slot="parentRow"
              slot-scope="{ rowData }"
              :source="rowData"
              @delete="deleteSource"
              @save="setSourceProp({
                id: $event.id,
                prop: 'name',
                value: $event.name,
              })"
            />
          </tf-table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapState, mapMutations } from 'vuex';

  const namespace = 'coverage/documentSources';

  /**
   * A table dependent on a 'coverage/documentSources" namespaced store module constisting of all non-census sources for associated product type to be displayed for the Coverage Overview page.
   *
   * @vuedoc
   * @exports Coverage/Tables/Sources
   * @category Components
   */
  export default {
    name: 'TableCoverageSources',
    components: {
      EmptyTableCell: () => import('@/components/EmptyTableCell'),
      SourceTableRow: () => import('@/components/SourceTableRow'),
    },
    props: {
      coverage: {
        type: Object,
        required: true,
      },
      /**
       * "Coverage Type" is used to determine what type of coverages show in the template.
       */
      coverageType: {
        type: String,
        default: 'current',
      },
      productTypeId: {
        type: [String, Number],
        required: true,
      },
    },
    computed: {
      ...mapState(namespace, {
        error: (state) => state.error,
        isLoaded: (state) => state.isLoaded,
        sources: (state) => state.data,
      }),
      /**
       * Evaluate the product type to see if it's a stop loss product type to return the appropriate category type to be used in the template/routes.
       *
       * @returns {string}
       */
      categoryType() {
        return this.isStopLoss ? 'medical' : 'non-medical';
      },
      /**
       * Get the document ID of the selected current coverage to use in the template.
       *
       * @returns {number}
       */
      documentId() {
        return this.coverage.document_id;
      },
      /**
       * Evaluate the source types for any non census sources to be used in the template.
       *
       * @returns {boolean}
       */
      hasNonCensusSources() {
        return this.sourceTypes.some(({ sources }) => sources.length);
      },
      /**
       * Evaluate the selected current coverages to see if it's a stop loss product.
       * This is used to determine what source type categories to show and in the template.
       *
       * @returns {number}
       */
      isStopLoss() {
        return this.$TF.Product.isStopLoss(this.coverage);
      },
      /**
       * Evaluate the category name to determine which types of categories to show in the template.
       *
       * @returns {Array}
       */
      sourceTypes() {
        const sourceTypes = [
          'ExperienceSource',
          'PolicySource',
          'RateSource',
        ].map((sourceType) => ({
          sources: [],
          type: this.isStopLoss ? `StopLoss::${sourceType}` : sourceType,
        }));

        this.sources.reduce((r, { id, name, type }) => {
          if (sourceTypes.some(({ type: sourceType }) => sourceType === type)) {
            const index = sourceTypes.findIndex((category) => category.type === type);

            sourceTypes[index].sources.push({ id, name });
          }

          return r;
        }, {});

        return sourceTypes;
      },
    },
    watch: {
      documentId: {
        /**
         * Watch for the document ID to change to load the data in the store module.
         *
         * @param {number} newValue
         */
        handler(newValue) {
          this.loadSources(newValue);
        },
        immediate: true,
      },
    },
    methods: {
      ...mapActions(namespace, ['loadSources', 'resetState']),
      ...mapMutations(namespace, ['setSourceProp', 'deleteSource']),
    },
  };
</script>

<style scoped lang="scss">
  .container-sources {
    border-bottom: 1px solid $tf-light-gray;
  }

  ::v-deep {
    .table {
      border-bottom: 0;
    }

    .cell-prop-name {
      padding: 0;
    }
  }

  // styled as flex since the tf-button child needs it.
  .router-link-add-files {
    display: flex;
  }
</style>
